import { useEffect, useMemo, useState } from 'react';
import type { ProjectKind } from '../data/types';
import { getTrainingTips, type TipPhase } from './trainingTips';
import './TrainingTip.css';

export interface TrainingTipProps {
  kind: ProjectKind;
  /** Changes when a new training run starts; resets the rotation so
   *  every run begins on a fresh shuffle instead of mid-cycle. */
  runKey: string | number;
  /** Which tip pool to draw from. Defaults to `'training'` — the
   *  gradient-descent narration. Pass `'extracting'` during the first
   *  feature-extraction phase, or `'post'` when the model is already
   *  trained and the kid is studying the loss curve. */
  phase?: TipPhase;
  /** Seconds each tip is fully visible before the next fades in.
   *  Defaults to 6, which reads as "stays for a few seconds and comes
   *  again" without rushing a second-line tip. */
  intervalSec?: number;
}

const FADE_MS = 350;

function shuffle(n: number): number[] {
  const arr = Array.from({ length: n }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = arr[i]!;
    const b = arr[j]!;
    arr[i] = b;
    arr[j] = a;
  }
  return arr;
}

/**
 * Tiny rotating "what's actually going on" ticker shown beside the
 * training progress bar. Cycles tips at `intervalSec` with a short
 * cross-fade. Tips are sampled without replacement per shuffle so the
 * kid does not see the same line twice in a single training run.
 *
 * The component is intentionally read-only and silent for screen
 * readers (no aria-live) — the surrounding progress block already
 * announces status, and reading a fresh tip every 6 seconds would
 * drown the meaningful announcements.
 */
export function TrainingTip({
  kind,
  runKey,
  phase = 'training',
  intervalSec = 6,
}: TrainingTipProps): JSX.Element {
  const tips = useMemo(() => getTrainingTips(kind, phase), [kind, phase]);
  const [order, setOrder] = useState<number[]>(() => shuffle(tips.length));
  const [position, setPosition] = useState(0);
  const [fade, setFade] = useState<'in' | 'out'>('in');

  // New training run → reshuffle and start over.
  useEffect(() => {
    setOrder(shuffle(tips.length));
    setPosition(0);
    setFade('in');
  }, [runKey, tips.length]);

  // Rotation engine: hold for (intervalSec - fade), fade out, swap, fade in.
  useEffect(() => {
    const visibleMs = Math.max(0, intervalSec * 1000 - FADE_MS);
    const fadeOut = window.setTimeout(() => setFade('out'), visibleMs);
    const advance = window.setTimeout(() => {
      setPosition((p) => {
        const next = p + 1;
        if (next >= order.length) {
          setOrder(shuffle(tips.length));
          return 0;
        }
        return next;
      });
      setFade('in');
    }, intervalSec * 1000);
    return () => {
      window.clearTimeout(fadeOut);
      window.clearTimeout(advance);
    };
  }, [position, order, tips.length, intervalSec]);

  const tip = tips[order[position] ?? 0] ?? '';

  return (
    <p className={`bl-train-tip bl-train-tip--${fade}`} data-testid="bl-train-tip">
      <span className="bl-train-tip__spark" aria-hidden="true">
        ✨
      </span>
      <span className="bl-train-tip__text">{tip}</span>
    </p>
  );
}
